import type React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import {
  LeaveDuration,
  Meridiem,
  type LeaveDayCount,
} from '@gokrew/models/leaves';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { InfoIcon } from './InfoIcon';
import { calcDuration } from '@/utility/timesheet';

interface LeaveDatesPreviewProps {
  leaveDuration?: LeaveDuration;
  leaveDate?: string | Date;
  startDate?: string | Date;
  endDate?: string | Date;
  meridiem?: Meridiem;
  /**
   * The server's count for these dates (the portal's day-count endpoint, fed
   * by `leaveDayCountParams`), so the preview equals what a saved request
   * deducts: holidays on their observed day, the employee's own branch.
   * Undefined while it loads: nothing is shown rather than a guess.
   */
  dayCount?: LeaveDayCount;
  startTime?: string;
  endTime?: string;
  workDayMinutes?: number;
}

export function LeaveDatesPreview({
  leaveDuration,
  leaveDate,
  startDate,
  endDate,
  meridiem,
  dayCount,
  startTime,
  endTime,
  workDayMinutes,
}: LeaveDatesPreviewProps) {
  const { t } = useTranslation(TranslationNS.LibsCommon);

  const NoApplicableDatesWarning = () => (
    <div className="flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 text-yellow-600" />
      <div>
        <div className="font-medium text-xs">
          {t('leave_dates_preview.no_applicable_dates.title')}
        </div>
        <div className="text-[10px]">
          {t('leave_dates_preview.no_applicable_dates.description')}
        </div>
      </div>
    </div>
  );

  const ExceedsWorkDayWarning = () => (
    <div className="flex items-start gap-2 rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-800">
      <Clock className="mt-0.5 h-4 w-4 text-orange-600 shrink-0" />
      <div>
        <div className="font-medium text-xs">
          {t('leave_dates_preview.exceeds_work_day.title')}
        </div>
        <div className="text-[10px]">
          {t('leave_dates_preview.exceeds_work_day.description')}
        </div>
      </div>
    </div>
  );

  const effectiveDaysLabel = t('leave_dates_preview.effective_days');
  const effectiveHoursLabel = t('leave_dates_preview.effective_hours');

  // ---------- FULL DAY ----------
  if (leaveDuration === LeaveDuration.FULL_DAY && leaveDate) {
    if (!dayCount) return null;
    if (!dayCount.days) return <NoApplicableDatesWarning />;
    return (
      <Result
        text={effectiveDaysLabel}
        value={t('leave_dates_preview.day_count', { count: dayCount.days })}
      />
    );
  }

  // ---------- HALF DAY ----------
  if (leaveDuration === LeaveDuration.HALF_DAY && leaveDate && meridiem) {
    if (!dayCount) return null;
    if (!dayCount.days) return <NoApplicableDatesWarning />;
    return (
      <Result
        text={effectiveDaysLabel}
        value={t('leave_dates_preview.day_count', { count: dayCount.days })}
      />
    );
  }

  // ---------- MULTI DAY ----------
  if (leaveDuration === LeaveDuration.MULTI_DAY && startDate && endDate) {
    if (!dayCount) return null;
    if (!dayCount.days) return <NoApplicableDatesWarning />;

    const excludedDays = dayCount.calendarDays - dayCount.days;

    return (
      <div className="flex flex-col gap-1">
        <Result
          text={effectiveDaysLabel}
          value={t('leave_dates_preview.day_count', { count: dayCount.days })}
        />
        {excludedDays > 0 && (
          <p className="text-xs text-gray-500">
            {t('leave_dates_preview.excluded_days', {
              totalDays: dayCount.calendarDays,
              excludedDays,
            })}
          </p>
        )}
      </div>
    );
  }

  // ---------- HOURLY ----------
  if (
    leaveDuration === LeaveDuration.HOURLY &&
    leaveDate &&
    startTime &&
    endTime &&
    workDayMinutes
  ) {
    if (!dayCount) return null;
    if (!dayCount.days) return <NoApplicableDatesWarning />;

    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const leaveMinutes = eh * 60 + em - (sh * 60 + sm);
    if (leaveMinutes <= 0) return null;

    if (leaveMinutes >= workDayMinutes) return <ExceedsWorkDayWarning />;

    const duration = calcDuration(startTime, endTime);
    return <Result text={effectiveHoursLabel} value={duration} />;
  }

  return null;
}

function Result({
  text,
  value,
  info,
}: {
  text: string;
  value: string;
  info?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm flex items-center gap-2">
      <span>
        <span className="font-medium">{text}</span> {value}
      </span>
      {info && <InfoIcon size={13} content={info} />}
    </div>
  );
}
